/**
 * Main onboarding page with token-based secure session.
 * State-specific rendering is delegated to extracted components.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { StepWizard } from "~/components/form-engine";
import type { FormValues } from "~/components/form-engine";
import {
  adminOnboardingConfig,
  submitOnboarding,
  useOnboardingToken,
  useVerificationStatus,
  CircularTimer,
  type SubmissionContext,
} from "~/features/onboarding";
import { STORAGE_KEYS, SECURITY_CONFIG } from "~/lib/utils";
import { TOKEN_CONFIG } from "~/server/constants";
import {
  LoadingState,
  InvalidTokenState,
  ExpiredTokenState,
  SuccessState,
  ErrorState,
} from "./onboarding/states";

type SubmissionState =
  | { status: "idle" }
  // sessionId se guarda al enviar: la verificación sigue consultándose aunque el token caduque
  | { status: "success"; sessionId: string | null; verificationRequested: boolean }
  | { status: "error"; message: string };

export function OnboardingPage() {
  const { t } = useTranslation();
  const [submissionState, setSubmissionState] = React.useState<SubmissionState>({
    status: "idle",
  });

  // Token validation
  const {
    isLoading: tokenLoading,
    isValid: tokenValid,
    isExpired,
    error: tokenError,
    payload: tokenPayload,
    isWarning,
    tokenId,
    onboardingId,
    isPaused,
    togglePause,
    remainingTime,
  } = useOnboardingToken();

  // Verificación de la CIEC con el SAT tras el envío (verification-status)
  const verification = useVerificationStatus(
    submissionState.status === "success" ? submissionState.sessionId : null,
    submissionState.status === "success" && submissionState.verificationRequested
  );

  // Build submission context from token
  const submissionContext = React.useMemo<SubmissionContext | undefined>(() => {
    if (!tokenPayload || !tokenId || !onboardingId) return undefined;
    return {
      tokenId,
      onboardingId,
      enterpriseId: tokenPayload.enterpriseId,
      partialRfc: tokenPayload.partialRfc,
      partialRazonSocial: tokenPayload.partialRazonSocial,
    };
  }, [tokenPayload, tokenId, onboardingId]);

  // Pre-fill form values from token with only the PREFIX of RFC and razonSocial
  // User must complete the rest as verification that they know the full values
  const initialValues = React.useMemo<FormValues>(() => {
    if (!tokenPayload) return {};

    // Extract first 4 characters of RFC as prefix (e.g., "XAXX" from "XAXX010101000")
    const rfcPrefix = tokenPayload.rfc ? tokenPayload.rfc.slice(0, 4) : "";

    // Extract first 5 characters of razonSocial as prefix
    const razonSocialPrefix = tokenPayload.razonSocial ? tokenPayload.razonSocial.slice(0, 5) : "";

    return {
      rfc: rfcPrefix,  // Only prefix - user must complete
      razonSocial: razonSocialPrefix,  // Only prefix - user must complete
      regimenFiscal: tokenPayload.regimenFiscal || "",  // Pre-fill if available
    };
  }, [tokenPayload]);

  const handleSubmit = async (values: FormValues) => {
    if (isExpired) {
      return;
    }

    try {
      if (values.satPassword !== values.confirmSatPassword) {
        throw new Error("Passwords do not match");
      }

      const result = await submitOnboarding(values, submissionContext);

      if (result.success) {
        setSubmissionState({
          status: "success",
          sessionId: submissionContext?.onboardingId ?? null,
          verificationRequested: result.verificationRequested === true,
        });
      } else {
        // Translate error message if it's a known i18n key
        const translatedMessage = result.message.startsWith("onboarding.error.")
          ? t(result.message)
          : result.message;
        setSubmissionState({
          status: "error",
          message: translatedMessage,
        });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("common.error");
      setSubmissionState({
        status: "error",
        message,
      });
    }
  };

  const [formKey, setFormKey] = React.useState(0);

  const handleReset = () => {
    setSubmissionState({ status: "idle" });
    if (typeof window !== "undefined") {
      // SECURITY: Use sessionStorage to match StepWizard persistence
      sessionStorage.removeItem(STORAGE_KEYS.FORM_STATE);
      sessionStorage.removeItem(STORAGE_KEYS.FORM_TIMESTAMP);
    }
    setFormKey((prev) => prev + 1);
  };

  // Success state: va antes que las comprobaciones del token, que caduca mientras se verifica
  if (submissionState.status === "success") {
    return <SuccessState verification={verification} />;
  }

  // Loading state
  if (tokenLoading) {
    return <LoadingState />;
  }

  // Token missing, invalid, or already used
  if (!tokenValid && tokenError !== "expired") {
    return <InvalidTokenState error={tokenError} />;
  }

  // Token expired
  if (isExpired) {
    return <ExpiredTokenState />;
  }

  // Error state (only show for non-validation errors)
  if (
    submissionState.status === "error" &&
    !submissionState.message.includes("correct")
  ) {
    return <ErrorState message={submissionState.message} onRetry={handleReset} />;
  }

  // Circular timer shown on the current active step
  // totalTime uses TOKEN_CONFIG.EXPIRATION_MS (10 minutes) for accurate progress display
  const timerIcon = (
    <CircularTimer
      remainingTime={remainingTime}
      totalTime={TOKEN_CONFIG.EXPIRATION_MS}
      isWarning={isWarning}
      isExpired={isExpired}
      isPaused={isPaused}
      onClick={togglePause}
      size={40}
      strokeWidth={3}
    />
  );

  // Normal form state with valid token
  return (
    <div className="w-full py-6 sm:py-12 px-0 sm:px-4">
      <div className="mx-auto max-w-2xl">
        {/* Form Wizard with timer integrated as current step icon */}
        <StepWizard
          key={formKey}
          config={adminOnboardingConfig}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          persistKey={STORAGE_KEYS.FORM_STATE}
          persistTimeout={SECURITY_CONFIG.SESSION_TIMEOUT_MS}
          activeStepIcon={timerIcon}
        />
      </div>
    </div>
  );
}
